import React, { useState, useEffect } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import Box from '@mui/material/Box';

import { sizeBoxCalendarSelected } from 'src/components/calendar/styles';
import { useStore } from 'src/context/StoreContext';
import { getToken } from 'src/utils/auth';
import { postAPI } from 'src/utils/api';

import ServerDay from './dateCalendar/ServerDay';
import CalendarHeader from './dateCalendar/CalendarHeader';
import DayOfWeekFormatter from './dateCalendar/DayOfWeekFormatter';

const initialValue = dayjs();

type DateCalendarCustomProps = {
};

const DateCalendarCustom: React.FC<DateCalendarCustomProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;
  const setDateSelectedStore = storeContext.setDateSelected;
  const dateSelectedStore = storeContext.dateSelected;
  const isToDay = storeContext.isToDay;
  const setIsToday = storeContext.setIsToday;

  const [daysAvalid, setDaysAvalid] = useState<string[]>();
  const [dateSelected, setDateSelected] = useState<Dayjs>(dayjs(storeContext.dateSelected));
  
  const handleScroll = (scrollToId: string | null) => {
    if (scrollToId) {
      const element = document.getElementById(scrollToId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
      }
    }
  };

  const handleChangeDateSelect = (date: Dayjs | null) => {
    if (date) {
      setDateSelected(date);
      setDateSelectedStore(date.format('YYYY-MM-DD'));
      handleScroll(date.format('YYYY-MM-DD'));
    }
  };

  const getDaysAvalid = async (month: string): Promise<string[] | undefined> => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      const data = {
        month
      }

      const res = await postAPI(`/calendar/get-available-days?token=${token}`, data);

      if (res.code === 200 && res.data.days) {
        const days = res.data.days;
        setDaysAvalid(days);

        return days.length > 0 ? days : undefined;
      } else {
        return undefined;
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleChangeMonth = async (yearMonth: string): Promise<string> => {
    const res = await getDaysAvalid(yearMonth);

    let date = yearMonth + '-01';

    if (res && res.length !== 0) {
      date = res[0];
    }

    const selectedStore = dayjs(dateSelectedStore);
    
    if (yearMonth === selectedStore.format('YYYY-MM')) {
      setDateSelected(selectedStore);  
    } else {
      setDateSelected(dayjs(date));
    }

    return date;
  };

  useEffect(() => {
    // setDateSelectedStore(initialValue.format('YYYY-MM-DD'))
    getDaysAvalid(dateSelected.format('YYYY-MM'));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isToDay === true) {
      setIsToday(false);
      setDateSelected(initialValue);
      setDateSelectedStore(initialValue.format('YYYY-MM-DD'))
      getDaysAvalid(initialValue.format('YYYY-MM'));
    }
    // eslint-disable-next-line
  }, [isToDay]);

  return (
    <Box sx={{ m: '0px auto' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          defaultValue={initialValue}
          value={dateSelected}
          onChange={(newValue) => {
            handleChangeDateSelect(newValue);
          }}
          showDaysOutsideCurrentMonth
          views={['year', 'month', 'day']}
          renderLoading={() => <DayCalendarSkeleton />}
          dayOfWeekFormatter={DayOfWeekFormatter}
          slots={{
            day: ServerDay,
            calendarHeader: CalendarHeader,
          }}
          slotProps={{
            day: {
              dayAvailability: daysAvalid
            } as any,
            calendarHeader: {
              getDayAvalid: handleChangeMonth
            } as any
          }}
          sx={{
            '& .MuiDayCalendar-weekContainer': { m: '0px' },
            '& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel': {
              // Header day: Sunday Monday S...
              height: '28px',
              fontSize: '10px',
              lineHeight: '12px',
              fontWeight: 500,
              color: store.palette.calendar.colorDayofWeek,
            },
            '& .MuiBadge-root': { alignItems: 'center' },
            '& .MuiBadge-root .Mui-selected': { ...sizeBoxCalendarSelected, bgcolor: store.palette.calendar.selected },
            '& .MuiBadge-root .Mui-selected:hover': { ...sizeBoxCalendarSelected, bgcolor: store.palette.calendar.selected },
            '& .MuiBadge-root .Mui-selected:focus': { ...sizeBoxCalendarSelected, bgcolor: store.palette.calendar.selected },
            maxHeight: '255px',
            maxWidth: '196px',
            backgroundColor: store.palette.calendar.bgColor,
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DateCalendarCustom;
